/**
 * DeduplicatePanel.jsx
 *
 * Fuzzy deduplication for Contacts and Firms tables.
 * - Loads all records, runs trigram similarity to find candidate pairs
 * - Shows confidence-ranked pairs (certain / high / medium)
 * - Per-pair: side-by-side field comparison with user-selectable merge
 * - Merge = PATCH winner + DELETE loser via existing backend APIs
 * - Dismissed pairs persisted in localStorage
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchAllFirms, updateContact, deleteContact, updateFirm, deleteFirm } from '../api/backend.js';

const BACKEND_URL = import.meta.env.VITE_BACKEND_BASE_URL ?? '';

async function fetchContactsForDedup(getToken) {
  const token = await getToken();
  const res = await fetch(`${BACKEND_URL}/api/contacts/for-dedup`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`for-dedup failed: ${res.status}`);
  const data = await res.json();
  return data.rows ?? data;
}

// ── Trigram similarity ────────────────────────────────────────────────────────

function trigramSet(str) {
  if (!str) return new Set();
  const s = `  ${String(str).toLowerCase().replace(/\s+/g, ' ').trim()}  `;
  const set = new Set();
  for (let i = 0; i < s.length - 2; i++) set.add(s.slice(i, i + 3));
  return set;
}

function trigramSim(a, b) {
  if (!a && !b) return 1;
  if (!a || !b) return 0;
  const sa = trigramSet(a);
  const sb = trigramSet(b);
  let inter = 0;
  for (const t of sa) { if (sb.has(t)) inter++; }
  const union = sa.size + sb.size - inter;
  return union === 0 ? 0 : inter / union;
}

// ── Field definitions ─────────────────────────────────────────────────────────

const CONTACT_MERGE_FIELDS = [
  { key: 'name',               label: 'Name' },
  { key: 'email',              label: 'Email' },
  { key: 'role',               label: 'Role / Title' },
  { key: 'firm_name',          label: 'Firm Name' },
  { key: 'bd_status',          label: 'BD Status' },
  { key: 'source',             label: 'Source' },
  { key: 'added_date',         label: 'Added Date' },
  { key: 'city',               label: 'City' },
  { key: 'state_province',     label: 'State' },
  { key: 'country_territory',  label: 'Country' },
  { key: 'sector_coverage',    label: 'Sector Coverage' },
  { key: 'industry_focus',     label: 'Industry Focus' },
  { key: 'job_function',       label: 'Job Function' },
  { key: 'institution_type',   label: 'Institution Type' },
  { key: 'side',               label: 'Side' },
  { key: 'asset_class',        label: 'Asset Class' },
  { key: 'dominant_orientation', label: 'Dom. Orientation' },
  { key: 'dominant_style',     label: 'Dom. Style' },
  { key: 'focus_orientation',  label: 'Focus Orientation' },
  { key: 'focus_strategy',     label: 'Focus Strategy' },
  { key: 'focus_market_cap',   label: 'Focus Mkt Cap' },
  { key: 'focus_region',       label: 'Focus Region' },
  { key: 'relevance_score',    label: 'Relevance Score' },
  { key: 'notes',              label: 'Notes' },
];

// Fields sent to PATCH /api/contacts/:id
const CONTACT_PATCH_ALLOWED = new Set([
  'name','email','phone','role','source','added_date','bd_status','notes',
  'sector_coverage','industry_focus','job_function','dominant_style','dominant_orientation',
  'institution_type','side','asset_class','city','state_province','country_territory',
  'focus_industry_macro','focus_industry_mid','focus_industry_micro',
  'focus_market_cap','focus_orientation','focus_region','focus_strategy','focus_style',
]);

const FIRM_MERGE_FIELDS = [
  { key: 'firm_name',     label: 'Firm Name' },
  { key: 'fund_type',     label: 'Fund Type' },
  { key: 'hq_city',       label: 'HQ City' },
  { key: 'aum',           label: 'AUM ($M)' },
  { key: 'expected_arr',  label: 'Expected ARR' },
  { key: 'strategy',      label: 'Strategy' },
  { key: 'bd_status',     label: 'BD Status' },
  { key: 'owner',         label: 'Owner' },
  { key: 'notes',         label: 'Notes' },
];

const FIRM_PATCH_ALLOWED = new Set([
  'firm_name','fund_type','aum_tier','strategy','hq_city',
  'expected_arr','arr_tier','notes','owner','bd_status','aum',
]);

// ── Dedup logic ───────────────────────────────────────────────────────────────

function contactCompleteness(c) {
  return CONTACT_MERGE_FIELDS.filter(({ key }) => {
    const v = c[key] ?? c[`_${key}`] ?? '';
    return String(v).trim().length > 0;
  }).length;
}

function firmCompleteness(f) {
  return FIRM_MERGE_FIELDS.filter(({ key }) => String(f[key] ?? '').trim().length > 0).length;
}

// Shared/generic emails that many people at a firm use — skip for dedup
const SHARED_EMAIL_BLOCKLIST = new Set([
  'capitalgroup_bd_inquiry@capgroup.com',
]);

function isSharedEmail(email) {
  if (!email) return false;
  if (SHARED_EMAIL_BLOCKLIST.has(email)) return true;
  // Common shared-inbox patterns
  const local = email.split('@')[0] ?? '';
  return /^(info|inquiry|inquiries|contact|team|sales|admin|hello|support|bd|ir|investor\.relations|noreply|no-reply)(\+.*)?$/.test(local);
}

function findContactDups(contacts, dismissed) {
  const pairs = [];

  // Pass 1 — O(n) exact email dedup via hashmap
  const byEmail = new Map();
  for (const c of contacts) {
    const email = String(c.email ?? '').toLowerCase().trim();
    if (!email || isSharedEmail(email)) continue;
    if (byEmail.has(email)) {
      const a = byEmail.get(email);
      const key = pairKey(a.contact_id, c.contact_id);
      if (dismissed.has(key)) continue;
      // Only "certain" if names are also very similar; otherwise high confidence
      const sameish = trigramSim(a.name, c.name) > 0.85;
      if (sameish) {
        pairs.push({ a, b: c, score: 1.0, confidence: 'certain', reason: 'Exact email + same name' });
      } else {
        pairs.push({ a, b: c, score: 0.8, confidence: 'high', reason: 'Same email, different names' });
      }
    } else {
      byEmail.set(email, c);
    }
  }

  // Pass 2 — sorted-neighborhood name dedup, O(n × window) instead of O(n²)
  // Sort by normalised name so similar names are adjacent; only compare within a sliding window.
  const WINDOW = 60;
  const sorted = [...contacts].sort((a, b) =>
    String(a.name ?? '').toLowerCase().localeCompare(String(b.name ?? '').toLowerCase())
  );

  for (let i = 0; i < sorted.length; i++) {
    const a = sorted[i];
    const emailA = String(a.email ?? '').toLowerCase().trim();
    const domainA = emailA.includes('@') ? emailA.split('@')[1] : '';

    for (let j = i + 1; j < sorted.length && j <= i + WINDOW; j++) {
      const b = sorted[j];

      // Skip if already found as email dup
      if (emailA && emailA === String(b.email ?? '').toLowerCase().trim()) continue;

      const key = pairKey(a.contact_id, b.contact_id);
      if (dismissed.has(key)) continue;

      const nameSim = trigramSim(a.name, b.name);
      if (nameSim < 0.45) continue;

      const emailB  = String(b.email ?? '').toLowerCase().trim();
      const domainB = emailB.includes('@') ? emailB.split('@')[1] : '';
      const firmA   = a.firm_name ?? '';
      const firmB   = b.firm_name ?? '';
      const firmSim  = firmA && firmB ? trigramSim(firmA, firmB) : 0;
      const sameFirm = a.firm_id && a.firm_id === b.firm_id;
      const sameDomain = domainA && domainB && domainA === domainB;

      let score = nameSim;
      if (sameFirm)       score = Math.min(1, score + 0.25);
      else if (firmSim > 0.7) score = Math.min(1, score + 0.15);
      else if (sameDomain)    score = Math.min(1, score + 0.10);

      if (score < 0.55) continue;

      const confidence = score >= 0.9 ? 'high' : 'medium';
      const reason = sameFirm ? 'Same name + same firm'
        : firmSim > 0.7 ? 'Same name + similar firm'
        : sameDomain ? 'Same name + same email domain'
        : 'Similar name';
      pairs.push({ a, b, score, confidence, reason });
    }
  }

  return pairs.sort((x, y) => y.score - x.score);
}

function findFirmDups(firms, dismissed) {
  const pairs = [];

  // Sorted neighborhood — sort by name, compare within a window
  const WINDOW = 40;
  const sorted = [...firms].sort((a, b) =>
    String(a.firm_name ?? '').toLowerCase().localeCompare(String(b.firm_name ?? '').toLowerCase())
  );

  for (let i = 0; i < sorted.length; i++) {
    const a = sorted[i];
    for (let j = i + 1; j < sorted.length && j <= i + WINDOW; j++) {
      const b = sorted[j];
      const key = pairKey(a.firm_id, b.firm_id);
      if (dismissed.has(key)) continue;

      const nameSim = trigramSim(a.firm_name, b.firm_name);
      if (nameSim < 0.82) continue;

      const citySim = a.hq_city && b.hq_city ? trigramSim(a.hq_city, b.hq_city) : 0;
      let score = nameSim;
      if (citySim > 0.7) score = Math.min(1, score + 0.08);

      if (score < 0.82) continue;

      const confidence = score >= 0.92 ? 'high' : 'medium';
      pairs.push({ a, b, score, confidence, reason: 'Similar firm name' });
    }
  }
  return pairs.sort((x, y) => y.score - x.score);
}

function pairKey(idA, idB) {
  return [idA, idB].sort().join('|');
}

// ── localStorage helpers ──────────────────────────────────────────────────────

const LS_DISMISSED_CONTACTS = 'dedup_dismissed_contacts_v1';
const LS_DISMISSED_FIRMS     = 'dedup_dismissed_firms_v1';

function loadDismissed(lsKey) {
  try { return new Set(JSON.parse(localStorage.getItem(lsKey) ?? '[]')); } catch { return new Set(); }
}
function saveDismissed(lsKey, set) {
  try { localStorage.setItem(lsKey, JSON.stringify([...set])); } catch {}
}

// ── Confidence badge ──────────────────────────────────────────────────────────

const CONF_STYLE = {
  certain: { background: '#fee2e2', color: '#b91c1c', label: 'Certain' },
  high:    { background: '#fef3c7', color: '#92400e', label: 'High' },
  medium:  { background: '#f1f5f9', color: '#475569', label: 'Medium' },
};

function ConfBadge({ confidence }) {
  const s = CONF_STYLE[confidence] ?? CONF_STYLE.medium;
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 99,
      background: s.background, color: s.color, textTransform: 'uppercase', letterSpacing: '.05em' }}>
      {s.label}
    </span>
  );
}

// ── Merge comparison UI ───────────────────────────────────────────────────────

function MergeDialog({ pair, mode, onMerge, onCancel, merging }) {
  const fields = mode === 'contacts' ? CONTACT_MERGE_FIELDS : FIRM_MERGE_FIELDS;
  const getComplete = mode === 'contacts' ? contactCompleteness : firmCompleteness;
  const getId = mode === 'contacts' ? (r) => r.contact_id : (r) => r.firm_id;

  const scoreA = getComplete(pair.a);
  const scoreB = getComplete(pair.b);
  const maxScore = fields.length;

  // Default: keep the more complete record, fill from the other
  const defaultWinner = scoreA >= scoreB ? 'a' : 'b';

  // selections[key] = 'a' | 'b' | 'custom'
  const [selections, setSelections] = useState(() => {
    const init = {};
    for (const { key } of fields) {
      const va = String(pair.a[key] ?? '').trim();
      const vb = String(pair.b[key] ?? '').trim();
      if (!va && vb) init[key] = 'b';
      else if (va && !vb) init[key] = 'a';
      else if (va === vb) init[key] = 'a';
      else init[key] = defaultWinner; // both have values → prefer more complete record
    }
    return init;
  });

  // Which record is the "winner" (kept) — user can flip
  const [keepSide, setKeepSide] = useState(defaultWinner);

  function getVal(side, key) {
    const rec = side === 'a' ? pair.a : pair.b;
    return String(rec[key] ?? '').trim();
  }

  function handleMerge() {
    // Build merged fields from selections
    const mergedFields = {};
    for (const { key } of fields) {
      const side = selections[key] ?? keepSide;
      const val  = getVal(side, key);
      mergedFields[key] = val;
    }
    // Winner = kept record, loser = deleted record
    const winner = keepSide === 'a' ? pair.a : pair.b;
    const loser  = keepSide === 'a' ? pair.b : pair.a;
    onMerge(getId(winner), getId(loser), mergedFields);
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10100,
      background: 'rgba(0,0,0,.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onCancel}>
      <div
        style={{
          background: 'var(--c-surface)', borderRadius: 14, width: 820, maxWidth: '96vw',
          maxHeight: '90vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 24px 64px rgba(0,0,0,.3)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--c-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>Merge {mode === 'contacts' ? 'Contacts' : 'Firms'}</div>
            <div style={{ fontSize: 12, color: 'var(--c-text-3)', marginTop: 2 }}>
              Select which value to keep for each field. The other record will be deleted.
            </div>
          </div>
          <button onClick={onCancel} style={{ background: 'none', border: 'none', fontSize: 20, color: 'var(--c-text-3)', cursor: 'pointer', lineHeight: 1, padding: '0 4px' }}>×</button>
        </div>

        {/* Record headers */}
        <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 1fr', gap: 0, padding: '12px 20px 8px', borderBottom: '1px solid var(--c-border)', flexShrink: 0 }}>
          <div />
          {['a', 'b'].map((side) => {
            const rec   = side === 'a' ? pair.a : pair.b;
            const score = side === 'a' ? scoreA : scoreB;
            const isWinner = keepSide === side;
            return (
              <div key={side} style={{ paddingLeft: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontWeight: 700, fontSize: 13, color: isWinner ? 'var(--c-primary)' : 'var(--c-text)' }}>
                    Record {side.toUpperCase()}
                  </span>
                  {isWinner && (
                    <span style={{ fontSize: 10, fontWeight: 700, background: 'var(--c-primary)', color: '#fff', padding: '1px 7px', borderRadius: 99 }}>KEEP</span>
                  )}
                  <button
                    onClick={() => setKeepSide(side)}
                    disabled={isWinner}
                    style={{ fontSize: 11, color: isWinner ? 'var(--c-text-3)' : 'var(--c-primary)', background: 'none', border: 'none', cursor: isWinner ? 'default' : 'pointer', padding: 0 }}
                  >
                    {isWinner ? '✓ Keeping this' : 'Keep this instead'}
                  </button>
                </div>
                <div style={{ fontSize: 11, color: 'var(--c-text-3)' }}>
                  {score}/{maxScore} fields filled
                  {score === Math.max(scoreA, scoreB) && scoreA !== scoreB && (
                    <span style={{ marginLeft: 5, color: '#16a34a', fontWeight: 600 }}>← more complete</span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: 'var(--c-text-3)', marginTop: 2 }}>
                  ID: {mode === 'contacts' ? rec.contact_id : rec.firm_id}
                </div>
              </div>
            );
          })}
        </div>

        {/* Field rows */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <tbody>
              {fields.map(({ key, label }) => {
                const va = getVal('a', key);
                const vb = getVal('b', key);
                const isDiff   = va !== vb;
                const bothEmpty = !va && !vb;
                const sel = selections[key] ?? keepSide;

                return (
                  <tr key={key} style={{ background: isDiff ? 'var(--c-bg-2, #f8fafc)' : undefined }}>
                    <td style={{ width: 160, padding: '7px 20px 7px 20px', color: 'var(--c-text-3)', fontSize: 11.5, fontWeight: 500, verticalAlign: 'middle', borderBottom: '1px solid var(--c-border)', whiteSpace: 'nowrap' }}>
                      {label}
                    </td>
                    {['a', 'b'].map((side) => {
                      const val = side === 'a' ? va : vb;
                      const isSelected = sel === side;
                      const isRecommended = side === defaultWinner && isDiff;
                      return (
                        <td
                          key={side}
                          style={{
                            padding: '7px 16px',
                            borderBottom: '1px solid var(--c-border)',
                            borderLeft: '1px solid var(--c-border)',
                            cursor: isDiff && !bothEmpty ? 'pointer' : 'default',
                            background: isSelected && isDiff ? 'rgba(59,130,246,.06)' : undefined,
                            verticalAlign: 'middle',
                          }}
                          onClick={() => isDiff && !bothEmpty && setSelections((s) => ({ ...s, [key]: side }))}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            {isDiff && !bothEmpty && (
                              <input
                                type="radio"
                                checked={isSelected}
                                onChange={() => setSelections((s) => ({ ...s, [key]: side }))}
                                style={{ margin: 0, flexShrink: 0, cursor: 'pointer', accentColor: 'var(--c-primary)' }}
                              />
                            )}
                            <span style={{
                              color: val ? 'var(--c-text)' : 'var(--c-text-3)',
                              fontStyle: val ? 'normal' : 'italic',
                              flex: 1,
                              wordBreak: 'break-word',
                            }}>
                              {val || '—'}
                            </span>
                            {isRecommended && val && (
                              <span style={{ fontSize: 9, fontWeight: 700, color: '#16a34a', flexShrink: 0 }}>★</span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 20px', borderTop: '1px solid var(--c-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ fontSize: 12, color: 'var(--c-text-3)' }}>
            ★ = recommended field (from more complete record) · Click a row or radio to choose
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn--ghost btn--sm" onClick={onCancel} disabled={merging}>Cancel</button>
            <button
              className="btn btn--primary btn--sm"
              onClick={handleMerge}
              disabled={merging}
              style={{ minWidth: 120 }}
            >
              {merging ? 'Merging…' : `Keep Record ${keepSide.toUpperCase()} & Delete ${keepSide === 'a' ? 'B' : 'A'}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Pair row ──────────────────────────────────────────────────────────────────

function PairRow({ pair, mode, onMerge, onDismiss }) {
  const nameA = mode === 'contacts' ? pair.a.name : pair.a.firm_name;
  const nameB = mode === 'contacts' ? pair.b.name : pair.b.firm_name;
  const subA  = mode === 'contacts' ? (pair.a._firm_name ?? pair.a.firm_name ?? pair.a.email ?? '') : (pair.a.hq_city ?? '');
  const subB  = mode === 'contacts' ? (pair.b._firm_name ?? pair.b.firm_name ?? pair.b.email ?? '') : (pair.b.hq_city ?? '');

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
      borderBottom: '1px solid var(--c-border)',
      background: 'var(--c-surface)',
    }}>
      <ConfBadge confidence={pair.confidence} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 600, fontSize: 13 }}>{nameA || '—'}</span>
          <span style={{ color: 'var(--c-text-3)', fontSize: 12 }}>vs</span>
          <span style={{ fontWeight: 600, fontSize: 13 }}>{nameB || '—'}</span>
        </div>
        <div style={{ display: 'flex', gap: 12, marginTop: 2, fontSize: 11.5, color: 'var(--c-text-3)' }}>
          <span>{subA}</span>
          {subB && subB !== subA && <><span>·</span><span>{subB}</span></>}
          <span style={{ color: 'var(--c-text-3)', fontStyle: 'italic' }}>{pair.reason}</span>
        </div>
      </div>

      <div style={{ fontSize: 11, color: 'var(--c-text-3)', whiteSpace: 'nowrap' }}>
        {Math.round(pair.score * 100)}% match
      </div>

      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        <button
          className="btn btn--ghost btn--sm"
          onClick={() => onDismiss(pair)}
          style={{ fontSize: 11 }}
        >
          Not same
        </button>
        <button
          className="btn btn--primary btn--sm"
          onClick={() => onMerge(pair)}
          style={{ fontSize: 11 }}
        >
          Review & Merge
        </button>
      </div>
    </div>
  );
}

// ── Session-level cache (survives panel close/reopen, cleared on merge) ───────
let _cachedContacts = null;
let _cachedFirms    = null;

// ── Main panel ────────────────────────────────────────────────────────────────

export default function DeduplicatePanel({ onClose, getToken, onDone }) {
  const [tab, setTab]           = useState('contacts'); // 'contacts' | 'firms'
  const [status, setStatus]     = useState('idle'); // idle | loading | scanning | done
  const [contacts, setContacts] = useState([]);
  const [firms, setFirms]       = useState([]);
  const [contactPairs, setContactPairs] = useState([]);
  const [firmPairs, setFirmPairs]       = useState([]);
  const [mergeTarget, setMergeTarget]   = useState(null); // pair being merged
  const [merging, setMerging]           = useState(false);
  const [mergeError, setMergeError]     = useState('');
  const [mergedCount, setMergedCount]   = useState(0);
  const [filter, setFilter]             = useState('all'); // 'all'|'certain'|'high'|'medium'

  const [dismissedContacts, setDismissedContacts] = useState(() => loadDismissed(LS_DISMISSED_CONTACTS));
  const [dismissedFirms,    setDismissedFirms]    = useState(() => loadDismissed(LS_DISMISSED_FIRMS));

  // ── Load + scan ─────────────────────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    async function run() {
      // Use cached data if available — skip network fetch
      if (_cachedContacts && _cachedFirms) {
        setContacts(_cachedContacts);
        setFirms(_cachedFirms);
        setStatus('scanning');
        setTimeout(() => {
          if (!alive) return;
          setContactPairs(findContactDups(_cachedContacts, loadDismissed(LS_DISMISSED_CONTACTS)));
          setFirmPairs(findFirmDups(_cachedFirms, loadDismissed(LS_DISMISSED_FIRMS)));
          setStatus('done');
        }, 30);
        return;
      }
      setStatus('loading');
      try {
        const [cs, fRes] = await Promise.all([
          fetchContactsForDedup(getToken),
          fetchAllFirms(getToken),
        ]);
        if (!alive) return;
        const fs = Array.isArray(fRes) ? fRes : (fRes?.rows ?? []);
        _cachedContacts = cs;
        _cachedFirms    = fs;
        setContacts(cs);
        setFirms(fs);
        setStatus('scanning');
        setTimeout(() => {
          if (!alive) return;
          setContactPairs(findContactDups(cs, loadDismissed(LS_DISMISSED_CONTACTS)));
          setFirmPairs(findFirmDups(fs, loadDismissed(LS_DISMISSED_FIRMS)));
          setStatus('done');
        }, 30);
      } catch (e) {
        console.error('[DeduplicatePanel] load error', e);
        if (alive) setStatus('done');
      }
    }
    run();
    return () => { alive = false; };
  }, [getToken]);

  // ── Dismiss ──────────────────────────────────────────────────────────────────
  function dismissPair(pair, mode) {
    const lsKey    = mode === 'contacts' ? LS_DISMISSED_CONTACTS : LS_DISMISSED_FIRMS;
    const idGetter = mode === 'contacts' ? (r) => r.contact_id : (r) => r.firm_id;
    const key      = pairKey(idGetter(pair.a), idGetter(pair.b));
    if (mode === 'contacts') {
      setDismissedContacts((prev) => { const next = new Set(prev); next.add(key); saveDismissed(lsKey, next); return next; });
      setContactPairs((prev) => prev.filter((p) => pairKey(p.a.contact_id, p.b.contact_id) !== key));
    } else {
      setDismissedFirms((prev) => { const next = new Set(prev); next.add(key); saveDismissed(lsKey, next); return next; });
      setFirmPairs((prev) => prev.filter((p) => pairKey(p.a.firm_id, p.b.firm_id) !== key));
    }
  }

  // ── Merge ────────────────────────────────────────────────────────────────────
  async function handleMerge(winnerId, loserId, mergedFields) {
    setMerging(true);
    setMergeError('');
    try {
      if (tab === 'contacts') {
        // Filter to patchable fields only
        const patch = {};
        for (const [k, v] of Object.entries(mergedFields)) {
          if (CONTACT_PATCH_ALLOWED.has(k)) patch[k] = v;
        }
        await updateContact(getToken, winnerId, patch);
        await deleteContact(getToken, loserId);
        // Evict loser from cache so next open reflects the merge
        if (_cachedContacts) _cachedContacts = _cachedContacts.filter((c) => c.contact_id !== loserId);
        setContactPairs((prev) => prev.filter(
          (p) => p.a.contact_id !== loserId && p.b.contact_id !== loserId &&
                 p.a.contact_id !== winnerId && p.b.contact_id !== winnerId
        ));
      } else {
        const patch = {};
        for (const [k, v] of Object.entries(mergedFields)) {
          if (FIRM_PATCH_ALLOWED.has(k)) patch[k] = v;
        }
        await updateFirm(getToken, winnerId, patch);
        await deleteFirm(getToken, loserId);
        if (_cachedFirms) _cachedFirms = _cachedFirms.filter((f) => f.firm_id !== loserId);
        setFirmPairs((prev) => prev.filter(
          (p) => p.a.firm_id !== loserId && p.b.firm_id !== loserId &&
                 p.a.firm_id !== winnerId && p.b.firm_id !== winnerId
        ));
      }
      setMergeTarget(null);
      setMergedCount((n) => n + 1);
      onDone?.();
    } catch (e) {
      setMergeError(e.message ?? 'Merge failed');
    } finally {
      setMerging(false);
    }
  }

  function resetDismissed() {
    if (tab === 'contacts') {
      const empty = new Set();
      saveDismissed(LS_DISMISSED_CONTACTS, empty);
      setDismissedContacts(empty);
      setContactPairs(findContactDups(contacts, empty));
    } else {
      const empty = new Set();
      saveDismissed(LS_DISMISSED_FIRMS, empty);
      setDismissedFirms(empty);
      setFirmPairs(findFirmDups(firms, empty));
    }
  }

  // ── Filtered pairs ───────────────────────────────────────────────────────────
  const pairs = tab === 'contacts' ? contactPairs : firmPairs;
  const filteredPairs = useMemo(() => {
    if (filter === 'all') return pairs;
    return pairs.filter((p) => p.confidence === filter);
  }, [pairs, filter]);

  const counts = useMemo(() => ({
    certain: pairs.filter((p) => p.confidence === 'certain').length,
    high:    pairs.filter((p) => p.confidence === 'high').length,
    medium:  pairs.filter((p) => p.confidence === 'medium').length,
  }), [pairs]);

  return (
    <>
      {/* Slide-in panel */}
      <div
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 9900,
          width: 680, maxWidth: '96vw',
          background: 'var(--c-surface)',
          borderLeft: '1px solid var(--c-border)',
          boxShadow: '-8px 0 40px rgba(0,0,0,.15)',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid var(--c-border)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={{ fontWeight: 700, fontSize: 16 }}>
              Find Duplicates
              {mergedCount > 0 && (
                <span style={{ marginLeft: 8, fontSize: 12, color: '#16a34a', fontWeight: 600 }}>
                  ✓ {mergedCount} merged
                </span>
              )}
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: 'var(--c-text-3)', cursor: 'pointer', lineHeight: 1, padding: '0 4px' }}>×</button>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
            {[
              { key: 'contacts', label: `Contacts (${contactPairs.length})` },
              { key: 'firms',    label: `Firms (${firmPairs.length})` },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => { setTab(key); setFilter('all'); }}
                style={{
                  padding: '5px 14px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  background: tab === key ? 'var(--c-primary)' : 'var(--c-bg-2, #f1f5f9)',
                  color: tab === key ? '#fff' : 'var(--c-text-2)',
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Status + filters */}
          {status === 'loading' && (
            <div style={{ fontSize: 12.5, color: 'var(--c-text-3)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <div className="spinner" style={{ width: 14, height: 14 }} /> Loading records…
            </div>
          )}
          {status === 'scanning' && (
            <div style={{ fontSize: 12.5, color: 'var(--c-text-3)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <div className="spinner" style={{ width: 14, height: 14 }} /> Scanning for duplicates…
            </div>
          )}
          {status === 'done' && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: 'var(--c-text-3)' }}>Filter:</span>
              {[
                { key: 'all',     label: `All (${pairs.length})` },
                { key: 'certain', label: `Certain (${counts.certain})` },
                { key: 'high',    label: `High (${counts.high})` },
                { key: 'medium',  label: `Medium (${counts.medium})` },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setFilter(key)}
                  style={{
                    fontSize: 11.5, fontWeight: filter === key ? 700 : 400,
                    padding: '2px 10px', borderRadius: 99, border: 'none', cursor: 'pointer',
                    background: filter === key ? 'var(--c-primary)' : 'var(--c-bg-2, #f1f5f9)',
                    color: filter === key ? '#fff' : 'var(--c-text-2)',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Pair list */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {status === 'done' && filteredPairs.length === 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 8 }}>
              <div style={{ fontSize: 36 }}>✅</div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>No duplicates found</div>
              <div style={{ fontSize: 12.5, color: 'var(--c-text-3)' }}>
                {pairs.length === 0
                  ? 'All records look unique.'
                  : `${pairs.length - filteredPairs.length} pairs filtered out.`}
              </div>
            </div>
          )}

          {filteredPairs.map((pair, i) => (
            <PairRow
              key={i}
              pair={pair}
              mode={tab}
              onMerge={(p) => { setMergeTarget(p); setMergeError(''); }}
              onDismiss={(p) => dismissPair(p, tab)}
            />
          ))}
        </div>

        {/* Footer note */}
        {status === 'done' && (
          <div style={{ padding: '10px 16px', borderTop: '1px solid var(--c-border)', fontSize: 11.5, color: 'var(--c-text-3)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <span>
              Scanned {tab === 'contacts' ? contacts.length : firms.length} records
              {(tab === 'contacts' ? dismissedContacts : dismissedFirms).size > 0 && (
                <> · {(tab === 'contacts' ? dismissedContacts : dismissedFirms).size} pair{(tab === 'contacts' ? dismissedContacts : dismissedFirms).size === 1 ? '' : 's'} hidden</>
              )}
            </span>
            {(tab === 'contacts' ? dismissedContacts : dismissedFirms).size > 0 && (
              <button
                onClick={resetDismissed}
                style={{ background: 'none', border: '1px solid var(--c-border)', borderRadius: 5, cursor: 'pointer', fontSize: 11, color: 'var(--c-text-2)', padding: '2px 8px', whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                Reset hidden pairs
              </button>
            )}
          </div>
        )}
      </div>

      {/* Backdrop */}
      <div
        style={{ position: 'fixed', inset: 0, zIndex: 9899, background: 'rgba(0,0,0,.3)' }}
        onClick={onClose}
      />

      {/* Merge dialog */}
      {mergeTarget && (
        <MergeDialog
          pair={mergeTarget}
          mode={tab}
          onMerge={handleMerge}
          onCancel={() => { setMergeTarget(null); setMergeError(''); }}
          merging={merging}
        />
      )}

      {/* Error toast */}
      {mergeError && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          zIndex: 10200, background: '#fef2f2', border: '1px solid #fecaca',
          borderRadius: 10, padding: '10px 18px', fontSize: 13, color: '#b91c1c',
          boxShadow: '0 4px 16px rgba(0,0,0,.12)',
        }}>
          ⚠️ {mergeError}
          <button onClick={() => setMergeError('')} style={{ marginLeft: 10, background: 'none', border: 'none', cursor: 'pointer', color: '#b91c1c', fontWeight: 700 }}>×</button>
        </div>
      )}
    </>
  );
}
